import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tempToken, setTempToken] = useState(null);
  const [requireSecond2FA, setRequireSecond2FA] = useState(false);

  const refreshToken = useCallback(async () => {
    try {
      const oldToken = localStorage.getItem('adminToken');
      if (!oldToken) return false;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${oldToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('adminToken', data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Vérifier si un nouveau token a été fourni
      const newToken = response.headers.get('X-New-Token');
      if (newToken) {
        localStorage.setItem('adminToken', newToken);
        return true;
      }

      if (response.ok) {
        const data = await response.json();
        return data.valid;
      }

      // Si le token est expiré ou invalide
      if (response.status === 401) {
        const data = await response.json();
        if (data.shouldRefresh) {
          // Tentative de rafraîchissement automatique du token
          const refreshResult = await refreshToken();
          if (refreshResult) {
            return true;
          }
        }
        localStorage.removeItem('adminToken');
        return false;
      }

      // Pour les autres erreurs, on garde le token (peut-être un problème temporaire de réseau)
      return true;
    } catch (error) {
      console.error('Token verification error:', error);
      // En cas d'erreur réseau, on garde le token et on considère qu'il est valide
      return true;
    }
  }, [refreshToken]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        const isValid = await verifyToken(token);
        setIsAuthenticated(isValid);
      }
      setLoading(false);
    };

    checkAuth();
  }, [verifyToken]);

  // Ajouter une vérification périodique du token
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        const isValid = await verifyToken(token);
        if (!isValid) {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, verifyToken]);

  const login = async (username, password, code) => {
    try {
      console.log('Sending login request...'); // Debug log

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, code }),
      });

      const data = await response.json();
      console.log('Server response:', data); // Debug log

      if (!response.ok) {
        throw new Error(data.error || 'Invalid credentials or 2FA code');
      }

      // Vérifier que la réponse contient les propriétés requises
      if (!data || !data.token) {
        console.error('Invalid server response format:', data);
        throw new Error('Invalid server response format');
      }

      // Stocker le token temporaire et mettre à jour l'état
      setTempToken(data.token);
      setRequireSecond2FA(true);

      return {
        requireSecond2FA: true,
        message: data.message || 'Please enter your second 2FA code'
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const verifySecond2FA = async (code) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/verify-second-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        throw new Error('Invalid second 2FA code');
      }

      const data = await response.json();
      localStorage.setItem('adminToken', data.token);
      setIsAuthenticated(true);
      setRequireSecond2FA(false);
      setTempToken(null);
      return { success: true };
    } catch (error) {
      console.error('Second 2FA verification error:', error);
      return { error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setRequireSecond2FA(false);
    setTempToken(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      login, 
      logout,
      requireSecond2FA,
      verifySecond2FA 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 